'use client';
import { Fragment, useState } from 'react';
import { useResource } from '@/lib/use-resource';

interface CorrData {
  symbols: string[];
  matrix: (number | null)[][];
  maxPair: { a: string; b: string; value: number } | null;
  minPair: { a: string; b: string; value: number } | null;
}

interface ProposedResult {
  symbol: string;
  correlations: Array<{ symbol: string; value: number | null }>;
  avg: number | null;
  dataDays?: number;
  fetchedFromFmp?: boolean;
  error?: string;
}

function corrColor(c: number | null): string {
  if (c == null || !Number.isFinite(c)) return '#1e293b';
  // -1 (red) → 0 (neutral grey) → +1 (green)
  if (c >= 0) {
    const intensity = Math.min(1, c);
    return `rgba(16, 185, 129, ${0.15 + intensity * 0.65})`;
  }
  const intensity = Math.min(1, -c);
  return `rgba(239, 68, 68, ${0.15 + intensity * 0.65})`;
}

export function CorrelationHeatmap({ dataUrl = '/api/portfolio-snapshot' }: { dataUrl?: string } = {}) {
  const { data: snap } = useResource<{ correlations?: CorrData }>(dataUrl);
  const data = snap?.correlations ?? null;

  // What-if: type a candidate symbol → server computes its correlation against
  // each existing holding using the same 252-day window as the on-disk matrix.
  // The proposed row is rendered with an amber outline to mark it as
  // hypothetical (not held).
  const [draftSymbol, setDraftSymbol] = useState('');
  const [proposed, setProposed] = useState<ProposedResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function addProposed(e: React.FormEvent) {
    e.preventDefault();
    const sym = draftSymbol.trim().toUpperCase();
    if (!sym) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/proposed-correlation?symbol=${encodeURIComponent(sym)}`);
      const j: ProposedResult & { error?: string } = await r.json();
      if (!r.ok || j.error) {
        setError(j.error || `Failed to compute (${r.status})`);
        setProposed(null);
      } else {
        setProposed(j);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'network error');
      setProposed(null);
    } finally {
      setLoading(false);
    }
  }

  function clearProposed() {
    setProposed(null);
    setError(null);
    setDraftSymbol('');
  }

  if (!data || data.symbols.length === 0) return null;

  const n = data.symbols.length;
  // Sum off-diagonal correlation per symbol → avg corr (excluding self)
  const avgCorr = data.symbols.map((_, i) => {
    let sum = 0, ct = 0;
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const c = data.matrix[i][j];
      if (c != null && Number.isFinite(c)) { sum += c; ct++; }
    }
    return ct > 0 ? sum / ct : null;
  });

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">Pairwise Correlations</h2>
        <div className="text-[10px] text-[#64748b]">
          {data.maxPair && (
            <>highest: <span className="text-emerald-400">{data.maxPair.a}↔{data.maxPair.b} {data.maxPair.value.toFixed(2)}</span> · </>
          )}
          {data.minPair && (
            <>lowest: <span className="text-red-400">{data.minPair.a}↔{data.minPair.b} {data.minPair.value.toFixed(2)}</span></>
          )}
        </div>
      </div>
      {/* Candidate input — type a symbol and see its proposed row. */}
      <form onSubmit={addProposed} className="flex items-center gap-2 mb-2 text-[11px]">
        <input
          type="text"
          value={draftSymbol}
          onChange={(e) => setDraftSymbol(e.target.value)}
          placeholder="Add candidate symbol (e.g. SPOT)"
          className="px-2 py-1 rounded bg-[#0a0e14] border border-[#334155] text-[#e2e8f0] focus:outline-none focus:border-amber-500/60 font-mono uppercase tracking-wider w-56"
          maxLength={10}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !draftSymbol.trim()}
          className="px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-wide text-[10px] font-semibold"
        >
          {loading ? 'computing…' : 'add'}
        </button>
        {proposed && (
          <button
            type="button"
            onClick={clearProposed}
            className="px-2 py-1 rounded text-[#94a3b8] hover:text-white uppercase tracking-wide text-[10px]"
          >
            clear
          </button>
        )}
        {error && <span className="text-red-400">{error}</span>}
        {proposed && !error && (
          <span className="text-[#64748b]">
            {proposed.symbol} · avg corr {proposed.avg != null ? proposed.avg.toFixed(2) : '—'}
            {proposed.dataDays ? ` · ${proposed.dataDays}d window` : ''}
            {proposed.fetchedFromFmp ? ' · fetched from FMP, cached' : ''}
          </span>
        )}
      </form>

      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4 overflow-x-auto">
        {/* Cells pinned to a fixed width so the alpha-zone (10 names) and
            portfolio (17 names) matrices render at the exact same cell size.
            Without this, `1fr` would stretch cells to fill the container. */}
        <div className="inline-grid" style={{ gridTemplateColumns: `auto repeat(${n}, 2.6rem) auto`, gap: 1 }}>
          {/* Header row */}
          <div />
          {data.symbols.map((s) => (
            <div key={`h-${s}`} className="flex justify-center items-end pb-1">
              <span className="text-[11px] text-[#94a3b8] font-mono whitespace-nowrap">
                {s}
              </span>
            </div>
          ))}
          <div className="text-[11px] text-[#94a3b8] uppercase tracking-wide pl-2 flex items-end">avg</div>

          {/* Matrix rows */}
          {data.symbols.map((sym, i) => (
            <Fragment key={`row-${sym}`}>
              <div className="text-[12px] text-[#cbd5e1] font-mono pr-2 py-1 text-right">
                {sym}
              </div>
              {data.matrix[i].map((c, j) => (
                <div
                  key={`c-${i}-${j}`}
                  title={`${data.symbols[i]} ↔ ${data.symbols[j]}: ${c == null ? 'n/a' : c.toFixed(2)}`}
                  className="aspect-square text-[10px] text-center flex items-center justify-center font-mono cursor-help min-w-[2.4rem]"
                  style={{ backgroundColor: corrColor(c), color: c == null || Math.abs(c) < 0.3 ? '#94a3b8' : '#0a0e14' }}
                >
                  {c == null ? '—' : c.toFixed(1)}
                </div>
              ))}
              <div className="text-[12px] tabular-nums pl-2 py-1 text-right text-[#cbd5e1]">
                {avgCorr[i] == null ? '—' : avgCorr[i]!.toFixed(2)}
              </div>
            </Fragment>
          ))}

          {/* Proposed candidate row — outlined in amber to mark "what-if". */}
          {proposed && proposed.correlations.length > 0 && (
            <Fragment key="row-proposed">
              <div className="text-[12px] text-amber-300 font-mono font-bold pr-2 py-1 text-right border-t border-amber-500/40">
                {proposed.symbol}
                <span className="block text-[9px] text-amber-500/70 font-normal uppercase tracking-wider">candidate</span>
              </div>
              {proposed.correlations.map((c, j) => (
                <div
                  key={`prop-${j}`}
                  title={`${proposed.symbol} ↔ ${c.symbol}: ${c.value == null ? 'n/a' : c.value.toFixed(2)}`}
                  className="aspect-square text-[10px] text-center flex items-center justify-center font-mono cursor-help border-t border-amber-500/40 min-w-[2.4rem]"
                  style={{ backgroundColor: corrColor(c.value), color: c.value == null || Math.abs(c.value) < 0.3 ? '#94a3b8' : '#0a0e14' }}
                >
                  {c.value == null ? '—' : c.value.toFixed(1)}
                </div>
              ))}
              <div className="text-[12px] tabular-nums pl-2 py-1 text-right text-amber-300 font-semibold border-t border-amber-500/40">
                {proposed.avg == null ? '—' : proposed.avg.toFixed(2)}
              </div>
            </Fragment>
          )}
        </div>
        <div className="text-[12px] text-[#64748b] mt-3 leading-relaxed">
          <span className="text-emerald-400">Green</span> = positive correlation (move together). <span className="text-red-400">Red</span> = negative (natural hedge).
          The <strong>avg</strong> column on the right is each name&apos;s average correlation to the rest of the book — low avg = strong diversifier.
          {proposed && (
            <>
              {' '}
              <strong className="text-amber-300">Amber row</strong> = candidate not in the book; correlations computed against the same 252-day window.
            </>
          )}
        </div>
      </div>
    </section>
  );
}
